/**
 * WORK-ORDER-HELPERS.JS - Utility Functions for Work Order Management
 * 
 * SUMMARY:
 * Collection of pure utility functions for work order creation and editing.
 * These functions handle data transformation, validation, and formatting for work orders.
 */

import { convertToReactSelectFormat } from "../../../../util/section-dropdown-format";

/**
 * Recursively collects all tasks from project data into a Map.
 * Handles nested stages and top-level sections with tasks (including "Variations").
 * 
 * @param {Array} projectData - Array of project sections/stages
 * @returns {Map<string, object>} Map of taskId → task (with subTasks and subcontractor data)
 * 
 * @example
 * const taskMap = collectTasksFromProjectData(projectSections);
 * const task = taskMap.get(taskId);
 */
export const collectTasksFromProjectData = (projectData) => {
  const taskMap = new Map();

  if (!Array.isArray(projectData)) return taskMap;

  const traverseSection = (section) => {
    // If section has nested stages, recurse
    if (Array.isArray(section.stages)) {
      section.stages.forEach(traverseSection);
    }

    // If section has tasks, add each task to the map
    if (Array.isArray(section.tasks)) {
      section.tasks.forEach(task => {
        if (task.id) {
          taskMap.set(task.id, task);
        }
      });
    }
  };

  projectData.forEach(traverseSection);

  return taskMap;
};

/**
 * Builds a formatted description from task, subtask, and item information.
 * Trims empty parts to avoid extra separators.
 * 
 * @param {string} taskName - Name of the task
 * @param {string} subTaskName - Name of the subtask
 * @param {string} itemDescription - Description from subcontractor item
 * @returns {string} Formatted description "TaskName - Subtask Name - Subcontractor description"
 * 
 * @example
 * buildDescription("Plumbing", "Install Pipes", "PVC Pipe 50mm");
 * // Returns: "Plumbing - Install Pipes - PVC Pipe 50mm"
 */
export const buildDescription = (taskName, subTaskName, itemDescription) => {
  const parts = [taskName, subTaskName, itemDescription].filter(part => part && part.trim());
  return parts.join(' - ');
};

/**
 * Normalizes a subcontractor item from JSON string or object format.
 * Handles both string (JSON) and object inputs safely.
 * 
 * @param {string|object} item - Subcontractor item (may be JSON string or object)
 * @returns {object} Normalized item with id, name, Cost, saleAmount, description
 * 
 * @example
 * const normalized = normaliseSubcontractorItem('{"id":1,"name":"Item","Cost":100}');
 * // Returns: { id: 1, name: "Item", Cost: 100, saleAmount: 100, description: "Item" }
 */
export const normaliseSubcontractorItem = (item) => {
  let parsedItem = item;

  // Parse JSON string if needed
  if (typeof item === 'string') {
    try {
      parsedItem = JSON.parse(item);
    } catch (e) {
      console.warn('Failed to parse subcontractor item:', e);
      return {
        id: null,
        name: '',
        Cost: 0,
        saleAmount: 0,
        description: ''
      };
    }
  }

  // Return normalized object with safe defaults
  return {
    id: parsedItem?.id || null,
    name: parsedItem?.name || '',
    Cost: parseFloat(parsedItem?.Cost || 0),
    saleAmount: parseFloat(parsedItem?.saleAmount || parsedItem?.Cost || 0),
    description: parsedItem?.description || parsedItem?.name || ''
  };
};

/**
 * Builds attribution dropdown options from section data.
 * Uses convertToReactSelectFormat with taskIncluded=true to preserve the hierarchical
 * tree structure that TaskMultiSelect expects. This provides the same visual hierarchy
 * as Purchase Order with sections, indentation, and attribution indicators.
 * 
 * @param {Array} sectionData - Original section data with is_attribution_level flags
 * @returns {Array} Hierarchical tree structure for TaskMultiSelect
 * 
 * @example
 * const options = buildAttributionOptions(projectSections);
 * // Returns hierarchical options with sections, tasks, and attribution levels
 */
export const buildAttributionOptions = (sectionData) => {
  if (!Array.isArray(sectionData) || sectionData.length === 0) return [];

  // Use convertToReactSelectFormat directly to preserve hierarchical structure
  // This gives us the same tree display as Purchase Order with sections, indentation, etc.
  return convertToReactSelectFormat(sectionData, null, true);
};

/**
 * Recursively searches for an option in a hierarchical tree structure.
 * TaskMultiSelect uses nested 'options' arrays for sections, so we need to search recursively
 * to find options that are nested inside section groups.
 * 
 * @param {Array} options - Hierarchical options array from convertToReactSelectFormat
 * @param {string} valueToFind - The value to search for
 * @returns {Object|null} The matching option object or null if not found
 * 
 * @example
 * const option = findOptionInTree(hierarchicalOptions, "task-123");
 * // Returns: { value: "task-123", label: "Task Name", ... }
 */
export const findOptionInTree = (options, valueToFind) => {
  if (!Array.isArray(options) || !valueToFind) return null;

  for (const option of options) {
    // Check if this option matches
    if (option.value === valueToFind) {
      return option;
    }

    // Recursively search in nested options (for sections with children)
    if (option.options && Array.isArray(option.options)) {
      const found = findOptionInTree(option.options, valueToFind);
      if (found) return found;
    }
  }

  return null;
};

/**
 * Formats selected tasks for MultiSelect display.
 * Shows task name with stage context for attribution tasks (e.g., "WATER (BASEMENT)").
 * This allows each task to be uniquely identified while showing its attribution context.
 * 
 * @param {Array} selectedTasks - Array of selected task objects
 * @returns {Array} Formatted array for MultiSelect display
 * 
 * @example
 * formatTasksForDisplay([
 *   { stageId: "stage-123", taskId: "task-456", taskName: "WATER (BASEMENT)" },
 *   { taskId: "task-789", taskName: "REGULAR TASK" }
 * ]);
 * // Returns: [
 * //   { label: "WATER (BASEMENT)", value: "task-456" },
 * //   { label: "REGULAR TASK", value: "task-789" }
 * // ]
 */
export const formatTasksForDisplay = (selectedTasks) => {
  return selectedTasks.map(task => ({
    label: task.taskName, // taskName already includes stage context for attribution tasks
    value: task.taskId,
    taskType: task.taskType,
    stagePath: task.stagePath
  }));
};

/**
 * Removes attribution flags from section data for dropdown display.
 * Creates a new copy of the data with is_attribution_level set to false.
 * This allows all tasks to be visible in dropdowns while preserving original data.
 * 
 * @param {Array} data - Section data with is_attribution_level flags
 * @returns {Array} Section data with attribution flags removed
 * 
 * @example
 * const filteredData = removeAttributionFlags(sectionData);
 * // All sections now have is_attribution_level: false for dropdown visibility
 */
export const removeAttributionFlags = (data) => {
  if (!Array.isArray(data)) return [];

  return data.map((section) => ({
    ...section,
    is_attribution_level: section.is_attribution_level,
    isHistoricalView: !!section.is_attribution_level,
    stages: section.stages ? removeAttributionFlags(section.stages) : [],
  }));
};
